import CheckGreen from "check-green.svg";
import cn from "classnames";
import Field from "components/shared/field";
import Loader from "components/shared/loader";
import Select, { customStyles } from "components/shared/select";
import Toggle from "components/shared/toggle";
import { paths } from "helpers/paths";
import { ymEvent } from "helpers/system";
import { UmamiManager } from "lib/umami";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { addCars, getCars } from "sources/relays";

const PAGE_SIZE = 20;
const SCROLL_LOAD_OFFSET = 80;

export default function AddCars(props) {
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [allCarsBrandTypeId, setAllCarsBrandTypeId] = useState(null);
    const [allCarsTypeId, setAllCarsTypeId] = useState(null);

    const tableScrollRef = useRef(null);
    const loadingNextPageRef = useRef(false);
    const allCarsEnabledRef = useRef(null);
    const allCarsBrandTypeIdRef = useRef(null);
    const allCarsTypeIdRef = useRef(null);
    const history = useHistory();
    const isProfileEmbedded = props.embeddedMode === "profile";
    const isRelayConnectionEmbedded = props.embeddedMode === "relayConnection";
    const isRegistrationPage = !isProfileEmbedded && !isRelayConnectionEmbedded;

    const setAllCarsBrandType = (value) => {
        allCarsBrandTypeIdRef.current = value;
        setAllCarsBrandTypeId(value);
    };

    const setAllCarsType = (value) => {
        allCarsTypeIdRef.current = value;
        setAllCarsTypeId(value);
    };

    const applySelectedValuesToLoadedCars = useCallback((loadedCars) => {
        return loadedCars.map((car) => ({
            ...car,
            ...(allCarsEnabledRef.current !== null ? { isEnabled: allCarsEnabledRef.current } : {}),
            ...(allCarsBrandTypeIdRef.current !== null ? { brandTypeId: allCarsBrandTypeIdRef.current } : {}),
            ...(allCarsTypeIdRef.current !== null ? { typeId: allCarsTypeIdRef.current } : {}),
        }));
    }, []);

    const loadCars = useCallback(
        (nextPage = 1) => {
            const isFirstPage = nextPage === 1;

            if (isFirstPage) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            return getCars({ page: nextPage, pageSize: PAGE_SIZE }).then((response) => {
                const responseCars = applySelectedValuesToLoadedCars(response);

                setCars((currentCars) => {
                    const nextCars = isFirstPage ? responseCars : [...currentCars, ...responseCars];

                    setHasMore(response.length >= PAGE_SIZE);

                    return nextCars;
                });
                setPage(nextPage);
                setLoading(false);
                setLoadingMore(false);
            });
        },
        [applySelectedValuesToLoadedCars],
    );

    const loadNextPage = useCallback(() => {
        if (loadingNextPageRef.current) {
            return;
        }

        loadingNextPageRef.current = true;
        loadCars(page + 1).finally(() => {
            loadingNextPageRef.current = false;
        });
    }, [loadCars, page]);

    useEffect(() => {
        loadCars();
    }, [loadCars]);

    useEffect(() => {
        const tableScroll = tableScrollRef.current;

        if (loading || loadingMore || !hasMore || !cars.length) {
            return;
        }

        if (isRegistrationPage) {
            if (document.documentElement.scrollHeight - window.innerHeight <= SCROLL_LOAD_OFFSET) {
                loadNextPage();
            }
            return;
        }

        if (!tableScroll) {
            return;
        }

        if (tableScroll.scrollHeight <= tableScroll.clientHeight) {
            loadNextPage();
        }
    }, [cars.length, hasMore, isRegistrationPage, loadNextPage, loading, loadingMore]);

    useEffect(() => {
        if (!isRegistrationPage || loading || loadingMore || !hasMore || !cars.length) {
            return;
        }

        const handlePageScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollBottom = document.documentElement.scrollHeight - scrollTop - window.innerHeight;

            if (scrollBottom <= SCROLL_LOAD_OFFSET) {
                loadNextPage();
            }
        };

        window.addEventListener("scroll", handlePageScroll, { passive: true });

        return () => window.removeEventListener("scroll", handlePageScroll);
    }, [cars.length, hasMore, isRegistrationPage, loadNextPage, loading, loadingMore]);

    const handleTableScroll = (event) => {
        if (loading || loadingMore || !hasMore) {
            return;
        }

        const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;

        if (scrollHeight - scrollTop - clientHeight <= SCROLL_LOAD_OFFSET) {
            loadNextPage();
        }
    };

    const setValue = (carId, { name, value }) => {
        if (name === "isEnabled") {
            allCarsEnabledRef.current = null;
        }
        if (name === "brandTypeId") {
            setAllCarsBrandType(null);
        }
        if (name === "typeId") {
            setAllCarsType(null);
        }

        setCars((currentCars) => currentCars.map((car) => (car.id === carId ? { ...car, [name]: value } : car)));
    };

    const setAllCarsEnabled = (value) => {
        allCarsEnabledRef.current = value;
        setCars((currentCars) => currentCars.map((car) => ({ ...car, isEnabled: value })));
    };

    const handleAllCarsBrandTypeChange = ({ value }) => {
        setAllCarsBrandType(value);

        if (value === null) {
            return;
        }

        setCars((currentCars) => currentCars.map((car) => ({ ...car, brandTypeId: value })));
    };

    const handleAllCarsTypeChange = ({ value }) => {
        setAllCarsType(value);

        if (value === null) {
            return;
        }

        setCars((currentCars) => currentCars.map((car) => ({ ...car, typeId: value })));
    };

    const saveCars = () => {
        if (!cars) {
            return;
        }

        ymEvent("SaveAndContinueButtonClicked");
        UmamiManager.track("SaveAndContinueButtonClicked");

        setLoading(true);

        const dataForSave = {
            cars: cars.reduce((acc, car) => {
                if (!car.isEnabled) {
                    return acc;
                }
                return [
                    ...acc,
                    {
                        id: car.id,
                        number: car.number,
                        typeId: car.typeId,
                        brandTypeId: car.brandTypeId,
                        sensorId: car.sensor?.id,
                    },
                ];
            }, []),
        };

        addCars(dataForSave).then((res) => {
            setLoading(false);
            if (res) {
                props.getOrg();
                if (props.onSaved) {
                    props.onSaved();
                    return;
                }
                history.push(paths.root);
            }
        });
    };

    const carsTable = () => {
        if (!cars?.length) {
            return null;
        }

        const allCarsEnabled = cars.every((car) => car.isEnabled);

        return (
            <>
                <div
                    ref={tableScrollRef}
                    className={cn("container-fluid table-responsive p-0", { "h-100": isRelayConnectionEmbedded })}
                    onScroll={handleTableScroll}
                    {...(isProfileEmbedded ? { style: { height: "calc(100vh - 420px)" } } : {})}
                >
                    <table className="table table-sm table-bordered m-0">
                        <thead className="bg-white sticky-header">
                            <tr>
                                <th>#</th>
                                <th>
                                    <div>Включено</div>
                                    <Tooltip overlay="Включить или выключить все машины" placement="top">
                                        <div>
                                            <Toggle
                                                className="justify-content-center"
                                                value={allCarsEnabled}
                                                onChange={setAllCarsEnabled}
                                            />
                                        </div>
                                    </Tooltip>
                                </th>
                                <th>ID</th>
                                <th>Номер машины</th>
                                <th>Наименование машины из системы мониторинга</th>
                                <th>Номер трекера</th>
                                <th className="w-150">
                                    <div className="d-flex align-items-center mb-1">
                                        <span>Бренд машины</span>
                                    </div>
                                    <Tooltip overlay="Выберите бренд для всех машин" placement="top">
                                        <div className="font-weight-normal text-left">
                                            <Select
                                                value={allCarsBrandTypeId}
                                                inputProps={{ name: "allCarsBrandTypeId" }}
                                                onChange={handleAllCarsBrandTypeChange}
                                                options={props.enums.carBrandType || []}
                                                placeholder="Для всех"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    ...customStyles,
                                                    menu: (styles) => ({ ...styles, zIndex: 11, width: "200px" }),
                                                }}
                                            />
                                        </div>
                                    </Tooltip>
                                </th>
                                <th className="w-150">
                                    <div className="d-flex align-items-center mb-1">
                                        <span>Тип машины</span>
                                    </div>
                                    <Tooltip overlay="Выберите тип для всех машин" placement="top">
                                        <div className="font-weight-normal text-left">
                                            <Select
                                                value={allCarsTypeId}
                                                inputProps={{ name: "allCarsTypeId" }}
                                                onChange={handleAllCarsTypeChange}
                                                options={props.enums.carType || []}
                                                placeholder="Для всех"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    ...customStyles,
                                                    menu: (styles) => ({ ...styles, zIndex: 11, width: "200px" }),
                                                }}
                                            />
                                        </div>
                                    </Tooltip>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {cars.map((car, i) => (
                                <tr key={car.id}>
                                    <td>{i + 1}</td>
                                    <td>
                                        <Toggle
                                            className="justify-content-center"
                                            value={car.isEnabled}
                                            onChange={(value) => setValue(car.id, { name: "isEnabled", value })}
                                        />
                                    </td>
                                    <td>{car.id}</td>
                                    <td>
                                        <Field
                                            onChange={(data) => setValue(car.id, data)}
                                            value={car.number}
                                            type="carNumber"
                                            inputProps={{
                                                name: "number",
                                                placeholder: "А 111 АА / 99",
                                                className: "form-control form-control-xs",
                                                style: { width: "100px" },
                                            }}
                                        />
                                    </td>
                                    <td>{car.externalName}</td>
                                    <td>{car.trackerDeviceNumber}</td>
                                    <td className="w-150">
                                        <Select
                                            value={car.brandTypeId}
                                            inputProps={{ name: "brandTypeId" }}
                                            onChange={(data) => setValue(car.id, data)}
                                            options={props.enums.carBrandType || []}
                                            menuPortalTarget={document.body}
                                            styles={{ ...customStyles }}
                                        />
                                    </td>
                                    <td className="w-150">
                                        <Select
                                            value={car.typeId}
                                            inputProps={{ name: "typeId" }}
                                            onChange={(data) => setValue(car.id, data)}
                                            options={props.enums.carType || []}
                                            menuPortalTarget={document.body}
                                            styles={{ ...customStyles }}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {loadingMore && (
                            <tfoot className="bg-ff-2c" style={{ position: "sticky", bottom: 0 }}>
                                <tr>
                                    <td colSpan={8}>
                                        <Loader />
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
                {isProfileEmbedded && (
                    <div className="mt-2 btn btn-sm btn-brand ml-auto" onClick={saveCars}>
                        Сохранить
                    </div>
                )}
            </>
        );
    };

    if (isRelayConnectionEmbedded) {
        return (
            <div className={cn("relay-connection-modal__cars", { loading })}>
                {loading && (
                    <div className="loader-snippet--disable-loader">
                        <Loader />
                    </div>
                )}
                <div className="relay-connection-modal__cars-title">Выберите транспорт для отображения на карте</div>
                <div className="relay-connection-modal__cars-subtitle">
                    Отметьте машины, которые нужно добавить в CARGO.RUN.
                </div>
                <div className="relay-connection-modal__cars-table">{carsTable()}</div>
                <div className="relay-connection-modal__footer">
                    <button className="r-form__button" type="button" onClick={saveCars}>
                        Сохранить
                    </button>
                </div>
            </div>
        );
    }

    if (isProfileEmbedded) {
        return (
            <div className={cn("h-100 pt-3", { loading })}>
                <div className="card card-body">{carsTable()}</div>
            </div>
        );
    }

    return (
        <>
            {loading && (
                <div className="loader-snippet--disable-loader">
                    <Loader />
                </div>
            )}
            <div className="registration-panel__wrap registration-panel__wrap--wide">
                <div className="registration-panel__title">
                    Отлично! Мониторинг подключен.&nbsp;
                    <span className="font-size-52">
                        <CheckGreen />
                    </span>
                </div>
                <div className="registration-panel__subtitle">
                    Переходим к выбору машин. <br />
                    <br />
                    Выберите машины, которые хотите видеть в CARGO.RUN.
                    <br />
                    Можно выбрать все — или только нужные
                </div>
                <div className="registration-panel__container">
                    <div className="registration-panel p-2">
                        <form className="d-flex flex-column w-100" autoComplete="off">
                            <div className="flex-grow-1 overflow-auto">{carsTable()}</div>
                        </form>
                    </div>
                </div>
                <div className="mt-3 mb-2 text-center">
                    <button className="r-form__button" onClick={saveCars}>
                        Запустить CR START
                    </button>
                </div>
                <div className="">Машины добавятся на карту — и вы сразу увидите их движение</div>
                <div className="r-steps">
                    <div className="r-steps__item r-steps__item--active">
                        <div className="r-steps__item-circle">1</div>
                        <div className="r-steps__item-text">ШАГ</div>
                    </div>
                    <div className="r-steps__line r-steps__line--active" />
                    <div className="r-steps__item r-steps__item--active">
                        <div className="r-steps__item-circle">2</div>
                        <div className="r-steps__item-text">ШАГ</div>
                    </div>
                    <div className="r-steps__line r-steps__line--active" />
                    <div className="r-steps__item r-steps__item--active">
                        <div className="r-steps__item-circle">3</div>
                        <div className="r-steps__item-text">ШАГ</div>
                    </div>
                </div>
            </div>
        </>
    );
}
